import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LogOut, Lock, ChevronLeft, Bell, Stethoscope, BadgeCheck } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useAppData } from '../hooks/useAppData';

const GREETINGS = [
  { before: 5,  text: 'לילה טוב' },
  { before: 12, text: 'בוקר טוב' },
  { before: 18, text: 'צהריים טובים' },
  { before: 22, text: 'ערב טוב' },
  { before: 24, text: 'לילה טוב' },
];

const NUDGES = [
  'יום נעים מלא בתובנות',
  'מי שמלווה אחרים, ראוי גם הוא לתשומת לב',
  'נשימה עמוקה אחת לפני שממשיכים הלאה',
  'התשתית הקטנה שאת/ה בונה משנה חיים',
  'רגע של שקט שייך גם לך',
];

function getGreeting() {
  const hour = new Date().getHours();
  return GREETINGS.find((g) => hour < g.before)?.text ?? 'שלום';
}

export default function TherapistProfile() {
  const { user, profile, loading } = useAppData();
  const [notifications, setNotifications] = useState(true);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const greeting = useMemo(() => getGreeting(), []);
  const nudge = useMemo(() => NUDGES[new Date().getDate() % NUDGES.length], []);

  const handleLogoutConfirm = async () => {
    await supabase.auth.signOut();
    window.location.reload();
  };

  return (
    <div className="flex flex-col h-full bg-background overflow-y-auto scrollbar-hide dir-rtl text-foreground">
      <div className="p-6 pb-28 space-y-7 w-full max-w-3xl mx-auto">

        {/* כותרת אישית ורשמית */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          <p className="text-sm text-muted-foreground">{greeting},</p>
          <h1 className="text-2xl font-bold min-h-[2rem]">
            {loading ? ' ' : `ד"ר ${profile?.full_name || 'מטפל/ת'}`}
          </h1>
        </motion.div>

        {/* כרטיס פרופיל */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="bg-card border border-border rounded-2xl p-6 flex items-center gap-4 shadow-sm"
        >
          <div className="w-16 h-16 shrink-0 bg-primary rounded-full flex items-center justify-center text-primary-foreground">
            <Stethoscope size={28} />
          </div>

          <div className="min-w-0">
            <h2 className="text-lg font-bold truncate">
              {loading ? ' ' : `ד"ר ${profile?.full_name || 'מטפל/ת'}`}
            </h2>
            <p className="text-muted-foreground text-sm truncate">{user?.email}</p>
            <div className="inline-flex items-center gap-1.5 mt-2 text-xs font-medium text-primary bg-primary/10 px-2.5 py-1 rounded-full">
              <BadgeCheck size={13} /> מטפל/ת פעיל/ה
            </div>
          </div>
        </motion.div>

        {/* תזכורת יומית */}
        <div className="border border-border rounded-2xl px-4 py-3 text-sm text-muted-foreground bg-card shadow-sm">
          {nudge}
        </div>

        {/* הגדרות */}
        <div className="space-y-3">
          <h3 className="text-muted-foreground text-xs font-bold uppercase tracking-wider px-1">הגדרות</h3>

          <div className="bg-card border border-border rounded-2xl overflow-hidden divide-y divide-border/60 shadow-sm">
            <button className="w-full flex items-center justify-between p-4 hover:bg-muted/60 transition-colors">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-primary/10 rounded-xl"><Lock size={18} className="text-primary" /></div>
                <span className="font-medium">שינוי סיסמה</span>
              </div>
              <ChevronLeft size={18} className="text-muted-foreground" />
            </button>

            <div className="w-full flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-primary/10 rounded-xl">
                  <Bell size={18} className="text-primary" />
                </div>
                <span className="font-medium">התראות</span>
              </div>
              <button
                onClick={() => setNotifications(!notifications)}
                aria-label="החלפת התראות"
                className={`w-12 h-6 rounded-full relative transition-colors duration-200 ${notifications ? 'bg-primary' : 'bg-muted'}`}
              >
                <span
                  className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm transition-all duration-200"
                  style={{ [notifications ? 'right' : 'left']: '0.25rem' } as React.CSSProperties}
                />
              </button>
            </div>
          </div>
        </div>

        <button
          onClick={() => setShowLogoutConfirm(true)}
          className="w-full bg-destructive/10 border border-destructive/20 text-destructive font-bold py-4 rounded-2xl flex items-center justify-center gap-2 hover:bg-destructive/15 transition-colors"
        >
          <LogOut size={20} /> התנתקות מהמערכת
        </button>
      </div>

      {/* מודאל אישור התנתקות */}
      <AnimatePresence>
        {showLogoutConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-primary/30 flex items-center justify-center p-4 dir-rtl"
            onClick={() => setShowLogoutConfirm(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              onClick={(e) => e.stopPropagation()}
              className="w-[90%] max-w-[320px] rounded-2xl p-6 shadow-2xl bg-card border border-border text-foreground"
            >
              <div className="flex flex-col items-center text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center text-destructive mb-4">
                  <LogOut size={32} />
                </div>
                <h3 className="text-xl font-bold mb-2">מתנתק/ת?</h3>
                <p className="text-muted-foreground text-sm">
                  האם אתה בטוח שברצונך לצאת מהחשבון?
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowLogoutConfirm(false)}
                  className="flex-1 font-bold py-3 rounded-xl transition-colors bg-muted text-muted-foreground hover:bg-muted/70"
                >
                  ביטול
                </button>
                <button
                  onClick={handleLogoutConfirm}
                  className="flex-1 bg-destructive hover:bg-destructive/90 text-destructive-foreground font-bold py-3 rounded-xl transition-colors"
                >
                  כן, צא
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
